export const SITE_URL = "https://randomface.win";
export const SHARE_TITLE = "10억 받고 얼굴 랜덤 돌리기";
export const SHARE_TEXT = "10억 받고 얼굴 랜덤 돌리기 🎲 너도 한 판 돌려봐!";

/**
 * Fetch the generated image (data URL or remote fal URL) into a File so it can
 * be attached to the native share sheet or downloaded. Returns null if the
 * source blocks CORS or fetch fails.
 */
export async function imageUrlToFile(imageUrl: string): Promise<File | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    const ext = blob.type.includes("png") ? "png" : "jpg";
    return new File([blob], `randomface-${Date.now()}.${ext}`, {
      type: blob.type || "image/jpeg",
    });
  } catch {
    return null;
  }
}

/** Trigger a browser download for a File. */
export function downloadFile(file: File): void {
  const objectUrl = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = file.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Trigger a browser download for the generated image. */
export async function saveImage(imageUrl: string): Promise<void> {
  const file = await imageUrlToFile(imageUrl);
  if (!file) {
    window.open(imageUrl, "_blank", "noopener,noreferrer");
    return;
  }
  downloadFile(file);
}

export type ShareOutcome = "shared" | "copied" | "unsupported";

/**
 * Native share sheet (Instagram / KakaoTalk / X … on mobile) with the file
 * attached. Falls back to copying the site link on desktop / unsupported.
 */
export async function shareFile(file: File | null): Promise<ShareOutcome> {
  const data: ShareData = { title: SHARE_TITLE, text: SHARE_TEXT, url: SITE_URL };
  if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    data.files = [file];
  }

  if (typeof navigator.share === "function") {
    try {
      await navigator.share(data);
      return "shared";
    } catch (error: unknown) {
      // User cancelled — treat as no-op, not a fallback.
      if (error instanceof DOMException && error.name === "AbortError") {
        return "shared";
      }
    }
  }

  const copied = await copyLink();
  return copied ? "copied" : "unsupported";
}

/** Share the raw generated image via the native share sheet. */
export async function shareImage(imageUrl: string): Promise<ShareOutcome> {
  return shareFile(await imageUrlToFile(imageUrl));
}

/** Open the X (Twitter) web intent with the site link. */
export function shareToX(): void {
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    SHARE_TEXT,
  )}&url=${encodeURIComponent(SITE_URL)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Copy the site link to the clipboard. Returns whether it succeeded. */
export async function copyLink(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(SITE_URL);
    return true;
  } catch {
    return false;
  }
}
